import { Alert } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { filter, map, values } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import * as Yup from 'yup';

import { p2pProvidersApi } from 'api';
import {
  CloseFormikDialogResult,
  CloseDialogFormikHandler,
  Dialog,
  FormControls,
  FormikNumericField,
  FormikYesNoRadioGroup,
  FormikSelect,
  Money,
} from 'components';
import {
  EnabledStatus,
  OrderStatus,
  OrderStatusDetails,
  OrderStatusReason,
  P2PProviderType,
  QueryKey,
} from 'enums';
import {
  useCurrencies,
  useMeta,
  usePrevious,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  P2PProvidersFilters,
  PayinOrder,
  ResolvePayinOrderDispute,
} from 'types';
import { formUtils, filtersUtils, orderUtils, validationUtils } from 'utils';

type Values = ResolvePayinOrderDispute;

type Props = {
  open: boolean;
  order: PayinOrder;
  error?: AxiosError | null;
  amountDisabled: boolean;
  onClose: CloseDialogFormikHandler<Values>;
};

export const ResolveDisputeDialog: React.FC<Props> = ({
  open,
  order,
  error,
  amountDisabled,
  onClose,
}) => {
  const { isManager, isTrader } = useUser();
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.resolve',
  });
  const prevOpen = usePrevious(open);
  const { getFiatCurrencySymbol } = useCurrencies();
  const isTraderDispute = useMemo(() => !order.p2pProviderOrderId, [order]);
  const [initialValues, setInitialValues] = useState<Values>({
    status: OrderStatus.Completed,
    amount: order.amount,
    ...(isTraderDispute && { statusReason: '' as OrderStatusReason }),
    p2pProviderId: '',
  });
  const { orderStatusReasons, disputeCompletedStatusReasons } =
    useUserContext();

  const manualResolveProvidersFilters: P2PProvidersFilters = useMemo(
    () => ({
      type: P2PProviderType.ManualResolve,
      fiatCurrencyId: order.fiatCurrencyId,
      assetCurrencyId: order.p2pProviderOrder?.assetCurrencyId,
      payinEnabled: EnabledStatus.Enabled,
    }),
    [order.fiatCurrencyId, order.p2pProviderOrder?.assetCurrencyId],
  );

  const queryResultProviders = useQuery(
    [QueryKey.P2PProviders, manualResolveProvidersFilters],
    () =>
      p2pProvidersApi.getSummary({
        filters: filtersUtils.encode(
          JSON.stringify(manualResolveProvidersFilters),
        ),
      }),
    { enabled: isManager && open },
  );

  const manualResolveProviderOptions = useMemo(
    () =>
      formUtils.getOptions(
        filter(
          queryResultProviders.data,
          (p) => p.id !== order.p2pProviderOrder?.provider?.id,
        ),
      ),
    [order.p2pProviderOrder?.provider?.id, queryResultProviders],
  );

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        status: Yup.mixed<OrderStatus>()
          .oneOf([OrderStatus.Completed, OrderStatus.Cancelled])
          .required(tCommon('errors.required')),
        amount: Yup.number()
          .moreThan(0, tCommon('errors.natural_number'))
          .required(tCommon('errors.required')),
        statusReason: Yup.mixed<OrderStatusReason>()
          .oneOf(values(OrderStatusReason))
          .optional(),
        p2pProviderId: Yup.string().optional(),
      }),
    [tCommon],
  );

  const currencySymbol = useMemo(
    () => getFiatCurrencySymbol(order?.fiatCurrencyId),
    [order, getFiatCurrencySymbol],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (!result.ok) {
        result.data?.formikHelpers.resetForm();
      }
      onClose(result);
    },
    [onClose],
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      handleClose({ ok: true, data: { values, formikHelpers } });
    },
    [handleClose],
  );

  const renderOrderStatus = useCallback(
    (status: string) => (
      <span>
        {`${t('resolve_as.prefix')} `}
        <span className="tw-font-bold">{status}</span>
      </span>
    ),
    [t],
  );

  const getStatusReasonOptions = useCallback(
    (status: OrderStatus) => {
      let statuses: OrderStatusReason[] = [];
      if (status === OrderStatus.Completed) {
        statuses = disputeCompletedStatusReasons;
      } else if (status === OrderStatus.Cancelled) {
        statuses = orderStatusReasons;
      }
      return map(statuses, (status) => ({
        label: orderUtils.getStatusReasonLabel(status),
        value: status,
      }));
    },
    [orderStatusReasons, disputeCompletedStatusReasons],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const statusReasonOptions = useMemo(
    () => getStatusReasonOptions(formik.values.status),
    [getStatusReasonOptions, formik.values.status],
  );

  const handleStatusChange = useCallback(() => {
    formik.setFieldValue('statusReason', '');
    formik.setFieldValue('p2pProviderId', '');
  }, [formik]);

  useEffect(() => {
    if (open && !prevOpen) {
      setInitialValues({
        status: OrderStatus.Completed,
        amount: order.amount,
        ...(!order.p2pProviderOrderId
          ? { statusReason: '' as OrderStatusReason }
          : {}),
        p2pProviderId: '',
      });
    }
  }, [open, prevOpen, order]);

  useEffect(() => {
    if (error) {
      formik.setErrors(
        validationUtils.getFormErrors(error, {
          amount: { too_big: tCommon('errors.not_enough_money') },
        }),
      );
    } else {
      formik.setErrors({});
    }
  }, [tCommon, formik, error]);

  const canReassignProvider = useMemo(
    () =>
      !isTraderDispute &&
      isManager &&
      formik.values.status === OrderStatus.Completed &&
      manualResolveProviderOptions.length > 0,
    [
      isTraderDispute,
      isManager,
      formik.values.status,
      manualResolveProviderOptions.length,
    ],
  );

  const { traderCanCancelDispute } = useMeta();
  const disputeCancelAvailable = useMemo(
    () =>
      isManager ||
      (isTrader &&
        traderCanCancelDispute &&
        !(
          order.statusDetails === OrderStatusDetails.DisputeRevertCancelled &&
          order.previousAmount
        )),
    [isManager, isTrader, traderCanCancelDispute, order],
  );

  return (
    <FormikProvider value={formik}>
      <Dialog
        open={open}
        title={t('title_dispute')}
        data={{ values: formik.values, formikHelpers: formik }}
        onClose={handleClose}
      >
        <Form>
          <Alert severity="info" sx={{ mb: 4, py: 0 }}>
            {formik.values.status === OrderStatus.Completed &&
              renderOrderStatus(t('resolve_as.completed'))}
            {disputeCancelAvailable &&
              formik.values.status === OrderStatus.Cancelled &&
              renderOrderStatus(t('resolve_as.cancelled'))}
          </Alert>
          {!!order.disputeAmount && (
            <div className="tw-mb-4">
              {`${tCommon('features.orders.table.columns.dispute_amount')}: `}
              <Money
                value={order.disputeAmount}
                fiatCurrencyId={order.fiatCurrencyId}
                symbol
              />
            </div>
          )}
          <FormControls>
            {disputeCancelAvailable && (
              <FormikYesNoRadioGroup
                name="status"
                label={t('payment_received')}
                yesValue={OrderStatus.Completed}
                noValue={OrderStatus.Cancelled}
                onChange={handleStatusChange}
              />
            )}
            {!order.p2pProviderOrderId && !!statusReasonOptions.length && (
              <FormikSelect
                name="statusReason"
                label={t('reason')}
                options={statusReasonOptions}
                noneOption
                disablePortal={false}
              />
            )}
            {formik.values.status === OrderStatus.Completed && (
              <FormikNumericField
                name="amount"
                disabled={amountDisabled}
                label={`${t('received_amount')} (${currencySymbol})`}
                allowNegative={false}
                required
                fullWidth
              />
            )}
            {canReassignProvider && (
              <FormikSelect
                name="p2pProviderId"
                label={t('manual_resolve_provider')}
                options={manualResolveProviderOptions}
                noneOption
                disablePortal={false}
              />
            )}
          </FormControls>
        </Form>
      </Dialog>
    </FormikProvider>
  );
};
